import { bookingAPI, paymentAPI } from '../services/api';

const STORAGE_KEY = 'bfp_pending_stay_booking';
const ORDER_KEY = 'bfp_pending_stay_order';
const STAY_KEY = 'bfp_pending_stay_id';

let reconcileInFlight = null;
let lastReconcileAt = 0;
const RECONCILE_COOLDOWN_MS = 8000;

export function markPendingStayBooking(bookingId, orderId, stayId) {
  if (bookingId) {
    sessionStorage.setItem(STORAGE_KEY, bookingId);
  }
  if (orderId) {
    sessionStorage.setItem(ORDER_KEY, orderId);
  }
  if (stayId) {
    sessionStorage.setItem(STAY_KEY, stayId);
  }
}

export function getPendingStayId() {
  return sessionStorage.getItem(STAY_KEY);
}

export function clearPendingStayBooking() {
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(ORDER_KEY);
  sessionStorage.removeItem(STAY_KEY);
}

function hasPendingCheckoutSession() {
  return Boolean(sessionStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(ORDER_KEY));
}

/** Cancel unpaid pending booking and release inventory. Idempotent on server. */
let abandonInFlight = null;

export async function releasePendingStayBooking(bookingId) {
  const id = bookingId || sessionStorage.getItem(STORAGE_KEY);
  if (!id) return false;

  if (abandonInFlight) return abandonInFlight;

  abandonInFlight = (async () => {
    try {
      await paymentAPI.abandonStayCheckout(id);
      if (sessionStorage.getItem(STORAGE_KEY) === id) {
        clearPendingStayBooking();
      }
      return true;
    } catch (err) {
      try {
        await bookingAPI.cancelBooking(id);
        if (sessionStorage.getItem(STORAGE_KEY) === id) {
          clearPendingStayBooking();
        }
        return true;
      } catch (fallbackErr) {
        console.warn(
          'Could not release pending stay booking:',
          err?.response?.data || err.message,
          fallbackErr?.response?.data || fallbackErr.message
        );
        return false;
      }
    } finally {
      abandonInFlight = null;
    }
  })();

  return abandonInFlight;
}

/**
 * Reconcile a leftover checkout (e.g. browser back from Cashfree).
 * Verifies gateway status first; only abandons when payment explicitly failed.
 */
export async function releaseStalePendingStayBooking() {
  if (!hasPendingCheckoutSession()) return;

  const now = Date.now();
  if (reconcileInFlight) return reconcileInFlight;
  if (now - lastReconcileAt < RECONCILE_COOLDOWN_MS) return;

  const bookingId = sessionStorage.getItem(STORAGE_KEY);
  const orderId = sessionStorage.getItem(ORDER_KEY);

  reconcileInFlight = (async () => {
    try {
      if (orderId) {
        try {
          const result = await paymentAPI.verifyPayment(orderId);
          if (result.paymentStatus === 'SUCCESS') {
            clearPendingStayBooking();
            return;
          }
          if (result.bookingStatus === 'CANCELLED') {
            clearPendingStayBooking();
            return;
          }
          if (result.paymentStatus === 'FAILED') {
            await releasePendingStayBooking(bookingId);
            return;
          }
          // ACTIVE / PENDING at gateway — do not release inventory
          return;
        } catch (err) {
          console.warn('Could not verify stale checkout payment:', err?.response?.data || err.message);
          return;
        }
      }

      // Without order id we cannot verify payment — do not call abandon (avoids spamming paid bookings)
    } finally {
      lastReconcileAt = Date.now();
      reconcileInFlight = null;
    }
  })();

  return reconcileInFlight;
}

function shouldAutoReconcileCheckout() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return (
    path.includes('/rooms') ||
    path.includes('/booking-review') ||
    path.includes('/stay-payment-return')
  );
}

/** Call when the tab becomes visible again (e.g. browser back from Cashfree). */
export function registerCheckoutAbandonListeners() {
  if (typeof window === 'undefined') return () => {};

  const reconcileIfNeeded = () => {
    if (shouldAutoReconcileCheckout()) {
      releaseStalePendingStayBooking();
    }
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      reconcileIfNeeded();
    }
  };

  const onPageShow = (event) => {
    if (event.persisted) {
      reconcileIfNeeded();
    }
  };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', onPageShow);

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', onPageShow);
  };
}
